import type { Dispatch, Ref, SetStateAction } from 'react';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import type Carousel from 'react-multi-carousel';

import type { WizardCarouselImage } from '@/app/[locale]/product/_steps/wizardData/types';
import type {
  ButtonGroupProps,
  ResponsiveType,
} from '@/app/components/MultiCarousel';
import MultiCarousel from '@/app/components/MultiCarousel';
import { RESPONSIVE_DATA } from '@/app/components/MultiCarousel/constants';

import SlideshowItem from '../_components/Slideshow/SlideshowItem';
import SlideshowNavigation from '../_components/Slideshow/SlideshowNavigation';
import { useDiscountContext } from '../_contexts/DiscountProvider';
import { useWizardStateContext } from '../_contexts/WizardStateProvider';
import showDiscountMessage from '../_utils/showDiscountMessage';
import getWizardData from './utils/getWizardData';

const RESPONSIVE = {
  desktop: { ...RESPONSIVE_DATA['desktop'], items: 1 },
  tablet: { ...RESPONSIVE_DATA['tablet'], items: 1 },
  mobile: { ...RESPONSIVE_DATA['mobile'], items: 1 },
} as ResponsiveType;

interface CustomButtonGroupProps extends ButtonGroupProps {
  readonly images: WizardCarouselImage[];
  readonly onChange: (nextSlide: number) => void;
}

const CustomButtonGroup = ({
  images,
  onChange,
  ...props
}: CustomButtonGroupProps) => (
  <SlideshowNavigation images={images} onChange={onChange} {...props} />
);

const CarouselImages = memo(function CarouselImages({
  setInverse,
}: {
  readonly setInverse: Dispatch<SetStateAction<boolean>> | undefined;
}) {
  const { urlDiscount, urlDiscountIsSuccess, urlPromotionData } =
    useDiscountContext();

  const { currentRoute, isAccessory, isOuraRing4Wizard } =
    useWizardStateContext();

  const [activeSlide, setActiveSlide] = useState<number>(0);

  const carouselRef = useRef<Carousel>(undefined);

  const showDiscount = showDiscountMessage({
    isOuraRing4Wizard,
    isAccessory,
    urlPromotionData,
    urlDiscount,
    urlDiscountIsSuccess,
  });

  const accessory = currentRoute.accessory;
  const style = currentRoute.style;
  const finish = currentRoute.finish;

  const wizard = getWizardData({
    accessory: accessory.value,
    style: style.value,
    finish: finish.value,
  });
  const styleStep = wizard?.styleStep;
  const finishStep = wizard?.finishStep;
  const sizeStep = wizard?.sizeStep;

  let images = styleStep?.carouselImages as WizardCarouselImage[] | null;

  switch (true) {
    case accessory.isValid:
      images = sizeStep?.carouselImages as WizardCarouselImage[] | null;
      break;
    case isOuraRing4Wizard:
      images = finishStep?.carouselImages as WizardCarouselImage[] | null;
      break;
  }

  const handleChange = useCallback((nextSlide: number) => {
    setActiveSlide(nextSlide);
  }, []);

  // Always go back to the first image when a different style is selected.
  useEffect(() => {
    setActiveSlide(0);
    carouselRef.current?.goToSlide(0);
  }, [style.value, finish.value]);

  useEffect(() => {
    setInverse?.(images?.[activeSlide]?.inverseText ?? false);
  }, [activeSlide, finish.value, images, setInverse]);

  if (!images) return null;

  return (
    <div className="size-full bg-gray-150">
      <MultiCarousel
        arrows={false}
        autoPlay={false}
        className="h-full"
        sliderClass="h-full"
        beforeChange={(nextIndex) => setActiveSlide(nextIndex)}
        customButtonGroup={
          <CustomButtonGroup images={images} onChange={handleChange} />
        }
        responsive={RESPONSIVE}
        ref={carouselRef as Ref<Carousel>}
        shouldResetAutoplay={false}
        showDots={false}
      >
        {images.map((props: WizardCarouselImage | null, idx) => {
          if (!props) return null;

          return (
            <SlideshowItem
              activeSlide={activeSlide}
              showDiscount={showDiscount}
              idx={idx}
              key={props.altMsgKey}
              {...props}
            />
          );
        })}
      </MultiCarousel>
    </div>
  );
});

export default CarouselImages;
